import { Button, IconButton, Typography } from "@povio/ui";
import { cx } from "class-variance-authority";

import { CloseIcon } from "@/assets/icons/general/Close";
import { AddIcon } from "@/assets/icons/ui/Add";
import type { ISectionProps } from "@/components/home/sections/ProfileBioSection";
import ProfileSectionCard from "@/components/home/sections/ProfileSectionCard";
import { CommonModels } from "@/data/common/common.models";
import { DemoContext } from "@/providers/demo/DemoContext";

interface IProps extends ISectionProps {
  skills?: CommonModels.SkillDto[];
  guestMode?: boolean;
}

const ProfileSkillsSection = ({ skills, isPublic = false, guestMode = false, onEditSectionClicked }: IProps) => {
  const { isDemoMode } = DemoContext.useDemo();
  // Filter out skills with empty names
  const filteredSkills = skills?.filter((item) => {
    return item.name && item.name.trim() !== "";
  });

  const hasSkills = () => {
    if (!filteredSkills) return false;
    return filteredSkills.length > 0;
  };

  const NOT_APPLIED_PROFICIENCY_LABEL = "Not Applied";

  const proficiencyColors = {
    [CommonModels.ProficiencyEnum.Foundational]: { text: "text-primary-500", border: "border-primary-500" },
    [CommonModels.ProficiencyEnum.Developing]: { text: "text-danger-200", border: "border-danger-200" },
    [CommonModels.ProficiencyEnum.Intermediate]: { text: "text-warning-300", border: "border-warning-300" },
    [CommonModels.ProficiencyEnum.Advanced]: { text: "text-blue-300", border: "border-blue-300" },
    [CommonModels.ProficiencyEnum.Expert]: { text: "text-success-500", border: "border-success-300" },
    [NOT_APPLIED_PROFICIENCY_LABEL]: { text: "text-neutral-300", border: "border-neutral-300" },
  };

  const skillsLegend = [
    {
      color: proficiencyColors[CommonModels.ProficiencyEnum.Expert].text,
      label: CommonModels.ProficiencyEnum.Expert,
      description: "Ex",
      number: 5,
    },
    {
      color: proficiencyColors[CommonModels.ProficiencyEnum.Advanced].text,
      label: CommonModels.ProficiencyEnum.Advanced,
      description: "Ad",
      number: 4,
    },
    {
      color: proficiencyColors[CommonModels.ProficiencyEnum.Intermediate].text,
      label: CommonModels.ProficiencyEnum.Intermediate,
      description: "In",
      number: 3,
    },
    {
      color: proficiencyColors[CommonModels.ProficiencyEnum.Developing].text,
      label: CommonModels.ProficiencyEnum.Developing,
      description: "De",
      number: 2,
    },
    {
      color: proficiencyColors[CommonModels.ProficiencyEnum.Foundational].text,
      label: CommonModels.ProficiencyEnum.Foundational,
      description: "Fo",
      number: 1,
    },
    {
      color: proficiencyColors[NOT_APPLIED_PROFICIENCY_LABEL].text,
      label: NOT_APPLIED_PROFICIENCY_LABEL,
      description: "Na",
      number: null,
    },
  ];

  const proficiencyToLabel = (proficiency: string | null) => {
    const proficiencyLabel = proficiency || NOT_APPLIED_PROFICIENCY_LABEL;

    return skillsLegend.find((skill) => skill.label === proficiencyLabel)?.description;
  };

  const proficiencyToNumber = (proficiency: string | null) => {
    const proficiencyLabel = proficiency || NOT_APPLIED_PROFICIENCY_LABEL;

    return skillsLegend.find((skill) => skill.label === proficiencyLabel)?.number;
  };

  const proficiencyToColor = (proficiency: string | null) => {
    if (!proficiency) return proficiencyColors[NOT_APPLIED_PROFICIENCY_LABEL];

    return (
      proficiencyColors[proficiency as CommonModels.ProficiencyEnum] || proficiencyColors[NOT_APPLIED_PROFICIENCY_LABEL]
    );
  };

  return (
    <ProfileSectionCard
      title="Skills"
      cardClassName="overflow-visible!"
      isEmpty={!hasSkills()}
      onEditSectionClicked={!isPublic && !guestMode ? onEditSectionClicked : undefined}
      isPublic={isPublic}
      actionButton={
        !guestMode && !isPublic && !isDemoMode ? (
          <Button
            icon={AddIcon}
            variant="outlined"
            size="s"
            width="hug"
            color="secondary"
            onPress={onEditSectionClicked}
          >
            Edit Skills
          </Button>
        ) : undefined
      }
    >
      <div className="mt-6 flex flex-row flex-wrap gap-3">
        {filteredSkills?.map((skill) => (
          <div
            key={skill.name}
            className={cx(
              "box-border flex w-auto flex-row items-center justify-center rounded-xs border bg-transparent px-2",
              !isPublic && "pr-0",
              isDemoMode && "pr-2",
              proficiencyToColor(skill.proficiency).border,
            )}
          >
            <div className="py-2">
              <Typography
                size="label-4"
                variant="prominent-1"
                className={cx(proficiencyToColor(skill.proficiency).text)}
                data-testid={`skill-name-${skill.name}`}
              >
                {proficiencyToLabel(skill.proficiency)}
                <sup>{proficiencyToNumber(skill.proficiency)}</sup>
              </Typography>
            </div>
            <div className={cx("mr-2 ml-1 h-full w-px border-r", proficiencyToColor(skill.proficiency).border)} />
            <Typography
              size="label-3"
              className={cx(guestMode && "pr-2")}
              data-testid={`skill-name-${skill.name}`}
            >
              {skill.name}
            </Typography>
            {!isPublic && !isDemoMode && !guestMode && (
              <IconButton
                variant="ghost"
                size="xs"
                label="Edit"
                color="secondary"
                className="[&>span>svg]:h-4 [&>span>svg]:w-4"
                onPress={onEditSectionClicked}
                icon={CloseIcon}
              />
            )}
          </div>
        ))}
        {!isPublic && !guestMode && filteredSkills?.length! < 6 && !isDemoMode && (
          <Button
            icon={AddIcon}
            variant="outlined"
            size="s"
            width="hug"
            color="secondary"
            onPress={onEditSectionClicked}
            className="mt-6"
          >
            Add More Skills
          </Button>
        )}
      </div>
    </ProfileSectionCard>
  );
};

export default ProfileSkillsSection;
